import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './ResultsSlideNew.css';
import { generateVTOAnonymous, generateVTOAnonymousWithAvatar, generateVTOShopper } from '../services/vtoService';
import { getSizeRecommendationAnonymous, getSizeRecommendationShopper, notifyAddToCart, computeProfile } from '../services/sizeRecommendationService';
import { saveUserDataToCache, saveAvatarToCache, updateCachedUserData } from '../services/cacheService';
import logger from '../services/logger';
import FitMannequin from './FitMannequin';
import MeasurementCorrectionPanel from './MeasurementCorrectionPanel';
import { computeBandConfig, getMorphologyCategory, getMannequinSrc } from '../utils/measurementUtils';
// import SaveAccountPrompt from './SaveAccountPrompt'; // Temporairement désactivé

function ResultsSlideNew({ userData, isAuthenticated, connectedUser, onRestart, onSaveAccount, onProfileCorrected, subscription, sessionId }) {
  const { t } = useTranslation('results');
  const { t: tc } = useTranslation('common');

  const [isLoadingSizeReco, setIsLoadingSizeReco] = useState(true);
  const [isLoadingVTO, setIsLoadingVTO] = useState(true);
  const [avatarImage, setAvatarImage] = useState('');
  const [sizeRecommendation, setSizeRecommendation] = useState(null);
  const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);
  const sizeScrollRef = useRef(null);
  const [errorSizeReco, setErrorSizeReco] = useState('');
  const [errorVTO, setErrorVTO] = useState('');
  const [isSharingVTO, setIsSharingVTO] = useState(false);
  const [vtoSkipped, setVtoSkipped] = useState(false);
  // Bloque définitivement l'affichage du VTO (spinner ou image) si la reco de taille
  // ne trouve aucune taille idéale — même si l'appel VTO était déjà en cours/terminé
  const [vtoBlockedNoIdealSize, setVtoBlockedNoIdealSize] = useState(false);
  const [cartStatus, setCartStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [cartError, setCartError] = useState('');
  const [showCorrectionPanel, setShowCorrectionPanel] = useState(false);
  const [isSavingCorrection, setIsSavingCorrection] = useState(false);
  const [correctionError, setCorrectionError] = useState('');
  // const [showSavePrompt, setShowSavePrompt] = useState(false); // Temporairement désactivé

  // Dérivé des mensurations calculées — même mannequin et mêmes bandes que MannequinAdjustSlide
  const mannequinCategory = getMorphologyCategory(
    userData.computedMeasurements, userData.height, userData.weight
  );
  const mannequinSrc = getMannequinSrc(userData.gender, mannequinCategory);
  const mannequinBandConfig = useMemo(
    () => computeBandConfig(
      userData.deltaChest ?? 0,
      userData.deltaWaist ?? 0,
      userData.deltaHips  ?? 0,
      userData.gender,
      mannequinCategory
    ),
    [userData.deltaChest, userData.deltaWaist, userData.deltaHips, userData.gender, mannequinCategory]
  );

  useEffect(() => {
    generateResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sizeRecommendation) return;
    const idx = sizeRecommendation.recommendedIndex;
    setSelectedSizeIndex(idx);

    // Centrer la taille recommandée dans le scroll au chargement
    setTimeout(() => {
      const container = sizeScrollRef.current;
      if (!container) return;
      const buttons = container.querySelectorAll('.size-option');
      const target = buttons[idx];
      if (!target) return;
      container.scrollLeft = target.offsetLeft - (container.offsetWidth - target.offsetWidth) / 2;
    }, 0);
  }, [sizeRecommendation]);

  // Écouter les réponses du parent (succès/erreur ajout panier)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'CART_ADD_SUCCESS') {
        setCartStatus('success');
        setCartError('');
      }
      if (event.data.type === 'CART_ADD_ERROR') {
        setCartStatus('error');
        setCartError(event.data.message || t('cartConfirmation.error'));
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [t]);

  const generateResults = async () => {
    // Sauvegarder les données en cache pour les utilisateurs non authentifiés
    if (!isAuthenticated) {
      saveUserDataToCache(userData);
    }

    const recoFullyActive = !!(subscription?.reco_activation && subscription?.size_reco_active);
    const vtoFullyActive  = !!(subscription?.vto_activation  && subscription?.vto_active);

    // Recommandation de taille
    if (recoFullyActive) {
      generateSizeRecommendation();
    } else if (subscription?.reco_activation && !subscription?.size_reco_active) {
      // Activé par le marchand mais plus de crédits → erreur directe, pas d'appel
      setErrorSizeReco(t('errors.serviceUnavailable'));
      setIsLoadingSizeReco(false);
    } else {
      // Non activé par le marchand → section masquée
      setIsLoadingSizeReco(false);
    }

    // VTO
    const hasVtoInput = !!(
      userData.avatarBase64 ||
      (isAuthenticated && userData.avatarUrl) ||
      userData.selfieBase64
    );

    if (vtoFullyActive && hasVtoInput) {
      generateVTOAvatar();
    } else if (vtoFullyActive && !hasVtoInput) {
      // Selfie passé et aucun avatar disponible → pas d'appel VTO, section masquée
      setVtoSkipped(true);
      setIsLoadingVTO(false);
    } else if (subscription?.vto_activation && !subscription?.vto_active) {
      // Activé par le marchand mais plus de crédits → erreur directe, pas d'appel
      setErrorVTO(t('errors.serviceUnavailable'));
      setIsLoadingVTO(false);
    } else {
      // Non activé par le marchand → section masquée
      setIsLoadingVTO(false);
    }
  };

  // overrideMeasurements permet de rafraîchir la reco juste après une correction
  // manuelle, sans attendre le re-render du prop userData (mise à jour côté App.js).
  const generateSizeRecommendation = async (overrideMeasurements) => {
    setIsLoadingSizeReco(true);
    setErrorSizeReco('');
    const computedMeasurements = overrideMeasurements ?? userData.computedMeasurements;

    try {
      logger.log('🚀 Appel gateway recommandation de taille...', {
        isAuthenticated,
        hasConnectedUser: !!connectedUser,
        customerDomain: userData.customerDomain,
        productId: userData.productId,
      });

      let result;

      if (isAuthenticated && connectedUser && userData.shopperId) {
        // Shopper connecté → endpoint shopper (la gateway récupère les données)
        const token = await connectedUser.getIdToken();
        result = await getSizeRecommendationShopper({
          customerDomain: userData.customerDomain,
          productId: userData.productId,
          userIdentifier: userData.shopperId,
          firebaseToken: token,
          sessionId,
        });
      } else {
        // Utilisateur anonyme → endpoint anonymous (on envoie les données)
        result = await getSizeRecommendationAnonymous({
          customerDomain: userData.customerDomain,
          productId: userData.productId,
          gender: userData.gender,
          height: userData.height,
          weight: userData.weight,
          age: userData.age,
          computedMeasurements,
          sessionId,
        });
      }

      setSizeRecommendation(result);
      logger.log('✅ Recommandation de taille reçue:', result);

      // Aucune taille idéale trouvée → le VTO ne doit pas s'afficher, même si son
      // chargement est en cours ou déjà terminé (voir rendu de la section avatar)
      const hasIdealSize = result.isOneSize || result.sizes.some((s) => s.isIdealMatch);
      if (!hasIdealSize) {
        setVtoBlockedNoIdealSize(true);
      }
    } catch (err) {
      logger.error('❌ Erreur lors de la recommandation de taille:', err);
      const msg = err.message || '';
      if (msg === 'PRODUCT_NOT_READY') {
        setErrorSizeReco(t('errors.productNotReady'));
      } else if (msg === 'SIZE_NO_DIMENSIONS') {
        setErrorSizeReco(t('errors.noSizeWithDimensions'));
      } else if (msg === 'SIZE_NO_MATCH') {
        setErrorSizeReco(t('errors.noSizeMatchesMeasurements'));
        setVtoBlockedNoIdealSize(true);
      } else {
        setErrorSizeReco(t('errors.sizeRecommendation'));
      }
    } finally {
      setIsLoadingSizeReco(false);
    }
  };

  // Applique une correction manuelle d'une ou plusieurs mesures classiques.
  // Les dimensions non éditées gardent leur delta en crans existant (résolu côté backend).
  const handleMeasurementCorrection = async (manualValues) => {
    setIsSavingCorrection(true);
    setCorrectionError('');

    try {
      const newComputedMeasurements = await computeProfile({
        gender: userData.gender,
        height: userData.height,
        weight: userData.weight,
        age: userData.age,
        deltaChest: userData.deltaChest,
        deltaWaist: userData.deltaWaist,
        deltaHips: userData.deltaHips,
        manualChest: manualValues.chest,
        manualWaist: manualValues.waist,
        manualHips: manualValues.hips,
      });

      onProfileCorrected(newComputedMeasurements, manualValues);

      if (!isAuthenticated) {
        updateCachedUserData({
          computedMeasurements: newComputedMeasurements,
          manualChest: manualValues.chest ?? userData.manualChest ?? null,
          manualWaist: manualValues.waist ?? userData.manualWaist ?? null,
          manualHips: manualValues.hips ?? userData.manualHips ?? null,
        });
      }

      setShowCorrectionPanel(false);
      await generateSizeRecommendation(newComputedMeasurements);
    } catch (err) {
      logger.error('❌ Erreur lors de la correction manuelle du profil:', err);
      setCorrectionError(t('errors.sizeRecommendation'));
    } finally {
      setIsSavingCorrection(false);
    }
  };

  const generateVTOAvatar = async () => {
    setIsLoadingVTO(true);
    setErrorVTO('');

    try {
      // Vérifier qu'on a les données gateway nécessaires
      if (!userData.customerDomain || !userData.productId) {
        throw new Error('customerDomain et productId requis pour le VTO');
      }

      let result;

      if (isAuthenticated && connectedUser && userData.avatarUrl && userData.shopperId) {
        // Shopper avec avatar existant → endpoint shopper
        // La gateway récupère l'avatar et l'image produit depuis les services backend
        logger.log('🚀 Appel VTO shopper via gateway...');
        const token = await connectedUser.getIdToken();
        result = await generateVTOShopper({
          customerDomain: userData.customerDomain,
          productId: userData.productId,
          userIdentifier: userData.shopperId,
          firebaseToken: token,
          sessionId,
        });
      } else if (!isAuthenticated && userData.avatarBase64) {
        // Utilisateur anonyme avec avatar en cache (2ème visite+) → endpoint avatar
        logger.log('🚀 Appel VTO anonymous-avatar via gateway...');
        result = await generateVTOAnonymousWithAvatar({
          avatarBase64: userData.avatarBase64,
          customerDomain: userData.customerDomain,
          productId: userData.productId,
          sessionId,
        });
      } else {
        // Premier VTO avec selfie → on sauvegarde le résultat comme avatar pour les prochaines fois
        const selfieToUse = userData.selfieBase64;
        if (!selfieToUse) {
          throw new Error(t('errors.missingSelfie'));
        }

        logger.log('🚀 Appel VTO anonymous via gateway...');
        result = await generateVTOAnonymous({
          selfieBase64: selfieToUse,
          customerDomain: userData.customerDomain,
          productId: userData.productId,
          gender: userData.gender,
          height: userData.height,
          weight: userData.weight,
          computedMeasurements: userData.computedMeasurements,
          sessionId,
        });

        // Sauvegarder le résultat comme avatar pour les visites suivantes
        if (result?.imageBase64) {
          saveAvatarToCache(result.imageBase64);
        }
      }

      logger.log('✅ Résultat VTO reçu');

      if (result.imageBase64) {
        setAvatarImage(result.imageBase64);
      }
    } catch (err) {
      logger.error('❌ Erreur lors de la génération VTO:', err);
      if (err.message === 'PRODUCT_NOT_READY') {
        setErrorVTO(t('errors.productNotReady'));
      } else {
        setErrorVTO(t('errors.vtoGeneration'));
      }
    } finally {
      setIsLoadingVTO(false);
    }
  };

  const handleShareVTO = async () => {
    if (!avatarImage || isSharingVTO) return;
    setIsSharingVTO(true);

    try {
      const response = await fetch(avatarImage);
      const blob = await response.blob();
      const file = new File([blob], 'essai-virtuel.png', { type: blob.type || 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: t('avatar.title') });
      } else {
        const link = document.createElement('a');
        link.href = avatarImage;
        link.download = 'essai-virtuel.png';
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      // AbortError = l'utilisateur a fermé la feuille de partage volontairement, pas une erreur
      if (err?.name !== 'AbortError') {
        logger.error('❌ Erreur lors du partage du VTO:', err);
      }
    } finally {
      setIsSharingVTO(false);
    }
  };

  // Temporairement désactivé
  // const handleSaveAccount = (data) => {
  //   // Ne pas cacher le prompt, le composant SaveAccountPrompt gérera l'affichage du succès
  //   // setShowSavePrompt(false);
  //   if (onSaveAccount) {
  //     onSaveAccount(data);
  //   }
  // };

  // const handleSkipSave = () => {
  //   setShowSavePrompt(false);
  // };

  // Disponibilité d'une taille. is_available (calculé par le SaaS) fait foi : il couvre
  // le stock inconnu (variante vendable sans suivi de stock, CSV sans colonne Stock),
  // que l'ancienne règle « stockQuantity null → épuisé » affichait à tort comme épuisé.
  // Repli sur le stock quand le service de recommandation ne renvoie pas is_available.
  const isSizeDataAvailable = (sizeData) => {
    if (!sizeData) return false;
    if (typeof sizeData.isAvailable === 'boolean') return sizeData.isAvailable;
    // stockQuantity null ou 0 → épuisé
    if (sizeData.stockQuantity === null || sizeData.stockQuantity === 0) return false;
    return true;
  };

  // Obtenir la taille sélectionnée actuelle
  const getSelectedSize = () => {
    if (sizeRecommendation?.isOneSize) return sizeRecommendation.sizes[0]?.size;
    return sizeRecommendation?.sizes[selectedSizeIndex]?.size || null;
  };

  // Vérifier si la taille sélectionnée est disponible
  const isSelectedSizeAvailable = () => {
    const sizeData = sizeRecommendation?.isOneSize
      ? sizeRecommendation.sizes[0]
      : sizeRecommendation?.sizes[selectedSizeIndex];
    return isSizeDataAvailable(sizeData);
  };

  const handleAddToCart = async () => {
    setCartStatus('loading');
    setCartError('');

    const selectedData = sizeRecommendation?.isOneSize
      ? sizeRecommendation.sizes[0]
      : sizeRecommendation?.sizes[selectedSizeIndex];

    const selectedSize = selectedData?.size;
    const selectedSizeId = selectedData?.sizeId;
    const fitType = sizeRecommendation?.isOneSize
      ? 'onesize'
      : selectedData?.isRecommended
        ? (selectedData?.isIdealMatch ? 'ideal' : 'approximate')
        : 'other';

    // Notifier la gateway de l'ajout au panier (tracking de la taille achetée)
    if (sizeRecommendation?.recommendationId && selectedSizeId) {
      try {
        await notifyAddToCart({
          customerDomain: userData.customerDomain,
          recommendationId: String(sizeRecommendation.recommendationId),
          selectedSizeId: String(selectedSizeId),
        });
      } catch (error) {
        logger.error('❌ Erreur notification add-to-cart (non bloquant):', error);
      }
    }

    // Envoyer un message à la page parente avec la taille sélectionnée
    window.parent.postMessage({
      type: 'ADD_TO_CART',
      size: selectedSize,
      fitType,
    }, '*');
  };

  const handleViewCart = () => {
    window.parent.postMessage({ type: 'CLOSE_AND_GO_TO_CART' }, '*');
  };

  const handleContinueShopping = () => {
    window.parent.postMessage({ type: 'MIRROR_CLOSED' }, '*');
  };

  const handleRetryCart = () => {
    setCartStatus('idle');
    setCartError('');
  };

  // Obtenir le label traduit du type de coupe
  const getFitTypeLabel = () => {
    if (sizeRecommendation?.isOneSize) return t('sizeRecommendation.oneSize.label');
    const selected = sizeRecommendation?.sizes[selectedSizeIndex];
    if (selected?.isRecommended) {
      return selected?.isIdealMatch
        ? t('sizeRecommendation.ideal.label')
        : t('sizeRecommendation.approximate.label');
    }
    return '';
  };

  // Flags dérivés de la prop subscription pour le rendu
  const showRecoSection  = !!(subscription?.reco_activation);
  const showVtoSection   = !!(subscription?.vto_activation) && !vtoSkipped;

  return (
    <div className="slide results-slide-new">
      <div className="slide-content">
        {/* Section Recommandation de taille */}
        {showRecoSection && <div className="recommendation-section">
          <div className="recommendation-header">
            <h2 className="recommendation-title">{t('sizeRecommendation.title')}</h2>
            {!isLoadingSizeReco && !errorSizeReco && userData.computedMeasurements && (
              <button
                type="button"
                className="correction-icon-btn"
                onClick={() => setShowCorrectionPanel((prev) => !prev)}
                aria-label={t('correction.toggleButton')}
                title={t('correction.toggleButton')}
              >
                <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.2 2.5a1.7 1.7 0 0 1 2.4 0l.9.9a1.7 1.7 0 0 1 0 2.4L7.4 16 3 17l1-4.4L14.2 2.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12.5 4.2 15.8 7.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
          </div>
          <div className="recommendation-body">
          <div className={`recommendation-content${showCorrectionPanel ? ' recommendation-content--covered' : ''}`} aria-hidden={showCorrectionPanel}>
          {isLoadingSizeReco ? (
            <div className="loading-section">
              <div className="spinner"></div>
              <p className="loading-text">
                {t('loading.sizeAnalysis')}
              </p>
            </div>
          ) : errorSizeReco ? (
            <div className="error-section">
              <p className="error-message">{errorSizeReco}</p>
              {/* Bouton retry uniquement si ce n'est pas une erreur de crédit */}
              {subscription?.size_reco_active !== false && (
                <button className="btn btn-secondary" onClick={() => generateSizeRecommendation()}>
                  {tc('buttons.retry')}
                </button>
              )}
            </div>
          ) : sizeRecommendation ? (
            <>
              {/* Affichage différent selon taille unique ou tailles multiples */}
              {sizeRecommendation.isOneSize ? (
                /* Cas Taille Unique */
                <div className="size-options one-size">
                  <div className="size-option active one-size-option" data-type="onesize">
                    <span className="size-label">{t('sizeRecommendation.oneSize.label')}</span>
                    <span className="size-value">{sizeRecommendation.sizes[0]?.size}</span>
                  </div>
                </div>
              ) : (
                /* Toutes les tailles défilantes */
                <div className="size-options-scroll" ref={sizeScrollRef}>
                  {sizeRecommendation.sizes.map((sizeData, index) => (
                    <button
                      key={sizeData.sizeId ?? index}
                      className={`size-option ${selectedSizeIndex === index ? 'active' : ''} ${!isSizeDataAvailable(sizeData) ? 'unavailable' : ''} ${sizeData.isNotRecommended ? 'not-recommended' : sizeData.isUncertain ? 'uncertain' : sizeData.isRecommended ? 'recommended' : ''}`}
                      onClick={() => setSelectedSizeIndex(index)}
                    >
                      {sizeData.isNotRecommended ? (
                        <span className="not-recommended-badge">{t('sizeRecommendation.notRecommendedBadge')}</span>
                      ) : sizeData.isUncertain ? (
                        <span className="uncertain-badge">{t('sizeRecommendation.uncertainBadge')}</span>
                      ) : sizeData.isRecommended && (
                        <span className="recommended-badge">{t('sizeRecommendation.recommendedBadge')}</span>
                      )}
                      <span className="size-label">
                        {sizeData.isRecommended
                          ? (sizeData.isIdealMatch ? t('sizeRecommendation.ideal.label') : t('sizeRecommendation.approximate.label'))
                          : '\u00A0'}
                      </span>
                      <span className="size-value">{sizeData.size}</span>
                      {!isSizeDataAvailable(sizeData) && (
                        <span className="unavailable-badge">{t('sizeRecommendation.unavailableBadge')}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Mannequin ou description selon la disponibilité des données de fit */}
              {(() => {
                const currentFitZones = sizeRecommendation.isOneSize
                  ? sizeRecommendation.sizes[0]?.fitZones
                  : sizeRecommendation.sizes[selectedSizeIndex]?.fitZones;
                const hasMannequin = sizeRecommendation.garmentType && currentFitZones?.length > 0;
                const selectedSize = sizeRecommendation.isOneSize
                  ? sizeRecommendation.sizes[0]
                  : sizeRecommendation.sizes[selectedSizeIndex];
                const selectedIsRecommended = sizeRecommendation.isOneSize || selectedSize?.isRecommended;
                const selectedIsIdeal = sizeRecommendation.isOneSize || selectedSize?.isIdealMatch;

                return (
                  <>
                    {selectedIsRecommended && (!hasMannequin || !selectedIsIdeal) && (
                      <div className="recommendation-card">
                        <p className="recommendation-text">
                          {sizeRecommendation.isOneSize
                            ? t('sizeRecommendation.oneSize.description')
                            : selectedIsIdeal
                              ? t('sizeRecommendation.ideal.description')
                              : t('sizeRecommendation.approximate.description')
                          }
                        </p>
                      </div>
                    )}
                    {hasMannequin && (
                      <FitMannequin
                        fitType={selectedIsRecommended ? (selectedIsIdeal ? 'ideal' : 'approximate') : 'fit'}
                        gender={userData.gender}
                        garmentType={sizeRecommendation.garmentType}
                        fitZones={currentFitZones}
                        mannequinSrc={mannequinSrc}
                        customBandConfig={mannequinBandConfig}
                      />
                    )}
                  </>
                );
              })()}

              {/* Bouton Ajouter au panier / Panneau de confirmation */}
              <div className="add-to-cart-section">
                {cartStatus === 'idle' && (
                  <button
                    className="btn btn-add-to-cart"
                    onClick={handleAddToCart}
                    disabled={!isSelectedSizeAvailable()}
                  >
                    {isSelectedSizeAvailable() ? t('addToCart.available') : t('addToCart.unavailable')}
                  </button>
                )}

                {cartStatus === 'loading' && (
                  <button className="btn btn-add-to-cart btn-add-to-cart--loading" disabled>
                    <span className="btn-spinner"></span>
                    {t('cartConfirmation.adding')}
                  </button>
                )}

                {cartStatus === 'success' && (
                  <div className="cart-confirmation">
                    <div className="cart-confirmation__header">
                      <svg className="cart-confirmation__check" viewBox="0 0 20 20" fill="none">
                        <path d="M6 10l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{t('cartConfirmation.added')}</span>
                    </div>
                    <div className="cart-confirmation__product">
                      {userData.clothingItem?.imageUrl && (
                        <img
                          className="cart-confirmation__image"
                          src={userData.clothingItem.imageUrl}
                          alt={userData.clothingItem?.title || ''}
                        />
                      )}
                      <div className="cart-confirmation__info">
                        <span className="cart-confirmation__title">{userData.clothingItem?.title}</span>
                        <span className="cart-confirmation__size">
                          {getSelectedSize()} · {getFitTypeLabel()}
                        </span>
                      </div>
                    </div>
                    <div className="cart-confirmation__actions">
                      <button className="btn btn-cart-primary" onClick={handleViewCart}>
                        {t('cartConfirmation.viewCart')}
                      </button>
                      <button className="btn btn-cart-secondary" onClick={handleContinueShopping}>
                        {t('cartConfirmation.continue')}
                      </button>
                    </div>
                  </div>
                )}

                {cartStatus === 'error' && (
                  <div className="cart-confirmation cart-confirmation--error">
                    <div className="cart-confirmation__header">
                      <span>{cartError || t('cartConfirmation.error')}</span>
                    </div>
                    <div className="cart-confirmation__actions">
                      <button className="btn btn-cart-primary" onClick={handleRetryCart}>
                        {tc('buttons.retry')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : null}
          </div>

          {/* Correction manuelle des mesures classiques — recouvre le contenu ci-dessus */}
          {showCorrectionPanel && !isLoadingSizeReco && !errorSizeReco && userData.computedMeasurements && (
            <MeasurementCorrectionPanel
              gender={userData.gender}
              computedMeasurements={userData.computedMeasurements}
              onCancel={() => { setShowCorrectionPanel(false); setCorrectionError(''); }}
              onSubmit={handleMeasurementCorrection}
              isSubmitting={isSavingCorrection}
              error={correctionError}
            />
          )}
          </div>
        </div>}

        {/* Section Avatar VTO */}
        {showVtoSection && <div className="avatar-section">
          <h2 className="avatar-title">{t('avatar.title')}</h2>
          {vtoBlockedNoIdealSize ? (
            <div className="error-section">
              <p className="error-message">{t('errors.noIdealSizeVto')}</p>
            </div>
          ) : isLoadingVTO ? (
            <div className="avatar-placeholder">
              <div className="placeholder-animation">
                <div className="spinner"></div>
                <p>{t('loading.avatar')}</p>
              </div>
            </div>
          ) : errorVTO ? (
            <div className="error-section">
              <p className="error-message">{errorVTO}</p>
              {/* Bouton retry uniquement si ce n'est pas une erreur de crédit */}
              {subscription?.vto_active !== false && (
                <button className="btn btn-secondary" onClick={() => generateVTOAvatar()}>
                  {tc('buttons.retry')}
                </button>
              )}
            </div>
          ) : avatarImage ? (
            <>
              <div className="avatar-container">
                <img src={avatarImage} alt={t('avatar.altText')} className="avatar-image" />
                <p className="ai-disclaimer">{t('avatar.aiDisclaimer')}</p>
              </div>
              <div className="avatar-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleShareVTO}
                  disabled={isSharingVTO}
                >
                  {tc('buttons.share')}
                </button>
              </div>
            </>
          ) : null}
        </div>}

        {/* Proposition de sauvegarde de compte - Temporairement masqué */}
        {/* {!isLoadingSizeReco && !isLoadingVTO && showSavePrompt && (
          <SaveAccountPrompt
            userData={userData}
            onSave={handleSaveAccount}
            onSkip={handleSkipSave}
          />
        )} */}

        <div className="navigation-buttons">
          <button className="btn btn-primary" onClick={onRestart}>
            {tc('buttons.restart')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResultsSlideNew;
